/* Movatic WMS — Frontend | CesarFilho 932048016 */
/* ADR-0035 — /settings CONFIGURA os indicadores (o /dashboard só MOSTRA).
 * Catálogo (GET /api/metrics/catalog) -> escolher; A minha vista -> ordenar + fixar.
 * Escreve via /api/metrics/preferences/ (CRUD scoped ao próprio utilizador). */
import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { useAuthStore } from '@/store/authStore'
import LanguageSwitcher from '@/components/LanguageSwitcher'

type Indicator = {
  id: number
  code: string
  name: string
  category: string
  unit: string
  is_active: boolean
}
type Pref = { id: number; indicator: number; position: number; pinned: boolean }

const BTN =
  'rounded border border-neutral-300 dark:border-neutral-600 px-2 py-0.5 text-xs ' +
  'hover:bg-neutral-100 dark:hover:bg-neutral-800 disabled:opacity-40 disabled:cursor-not-allowed'

function unwrap<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[]
  return ((data as { results?: T[] } | null)?.results ?? []) as T[]
}

function IndicatorsSettings() {
  const qc = useQueryClient()

  const catalog = useQuery({
    queryKey: ['metrics-catalog'],
    queryFn: async () =>
      unwrap<Indicator>((await api.get('/api/metrics/catalog')).data),
  })
  const prefs = useQuery({
    queryKey: ['metrics-preferences'],
    queryFn: async () =>
      unwrap<Pref>((await api.get('/api/metrics/preferences/')).data),
  })

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['metrics-preferences'] })
    qc.invalidateQueries({ queryKey: ['dashboard-metrics'] }) // o dashboard reflete já
  }

  const addPref = useMutation({
    mutationFn: (indicatorId: number) =>
      api.post('/api/metrics/preferences/', {
        indicator: indicatorId,
        position: prefs.data?.length ?? 0,
        pinned: false,
      }),
    onSuccess: invalidate,
  })
  const removePref = useMutation({
    mutationFn: (prefId: number) =>
      api.delete(`/api/metrics/preferences/${prefId}/`),
    onSuccess: invalidate,
  })
  const togglePin = useMutation({
    mutationFn: (p: Pref) =>
      api.patch(`/api/metrics/preferences/${p.id}/`, { pinned: !p.pinned }),
    onSuccess: invalidate,
  })
  const move = useMutation({
    mutationFn: ({ a, b }: { a: Pref; b: Pref }) =>
      Promise.all([
        api.patch(`/api/metrics/preferences/${a.id}/`, {
          position: b.position,
        }),
        api.patch(`/api/metrics/preferences/${b.id}/`, {
          position: a.position,
        }),
      ]),
    onSuccess: invalidate,
  })
  const busy =
    addPref.isPending ||
    removePref.isPending ||
    togglePin.isPending ||
    move.isPending

  const prefByIndicator = useMemo(() => {
    const m = new Map<number, Pref>()
    for (const p of prefs.data ?? []) m.set(p.indicator, p)
    return m
  }, [prefs.data])

  const ordered = useMemo(
    () => [...(prefs.data ?? [])].sort((a, b) => a.position - b.position),
    [prefs.data],
  )

  if (catalog.isLoading || prefs.isLoading)
    return <div className="text-sm text-neutral-500">A carregar…</div>
  if (catalog.isError)
    return (
      <div className="text-sm text-rose-500">Falha a carregar o catálogo.</div>
    )

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Catálogo: escolher */}
      <div>
        <h3 className="text-sm font-semibold mb-2">Catálogo de indicadores</h3>
        <ul className="space-y-1">
          {(catalog.data ?? []).map((ind) => {
            const pref = prefByIndicator.get(ind.id)
            return (
              <li
                key={ind.id}
                className="flex items-center justify-between gap-2 text-sm"
              >
                <span>
                  <span className="text-neutral-500">[{ind.category}]</span>{' '}
                  {ind.name}
                </span>
                <button
                  className={BTN}
                  disabled={busy}
                  onClick={() =>
                    pref ? removePref.mutate(pref.id) : addPref.mutate(ind.id)
                  }
                >
                  {pref ? 'Remover' : 'Mostrar'}
                </button>
              </li>
            )
          })}
        </ul>
      </div>

      {/* A minha vista: ordenar + fixar */}
      <div>
        <h3 className="text-sm font-semibold mb-2">A minha vista</h3>
        {ordered.length === 0 ? (
          <div className="text-sm text-neutral-500">
            Sem indicadores escolhidos — o dashboard mostra o catálogo por
            omissão.
          </div>
        ) : (
          <ul className="space-y-1">
            {ordered.map((p, i) => {
              const ind = (catalog.data ?? []).find((c) => c.id === p.indicator)
              return (
                <li
                  key={p.id}
                  className="flex items-center justify-between gap-2 text-sm"
                >
                  <span>
                    {p.pinned ? '★ ' : ''}
                    {ind?.name ?? `#${p.indicator}`}
                  </span>
                  <span className="flex gap-1">
                    <button
                      className={BTN}
                      disabled={busy || i === 0}
                      onClick={() => move.mutate({ a: p, b: ordered[i - 1] })}
                    >
                      ↑
                    </button>
                    <button
                      className={BTN}
                      disabled={busy || i === ordered.length - 1}
                      onClick={() => move.mutate({ a: p, b: ordered[i + 1] })}
                    >
                      ↓
                    </button>
                    <button
                      className={BTN}
                      disabled={busy}
                      onClick={() => togglePin.mutate(p)}
                    >
                      {p.pinned ? 'Desafixar' : 'Fixar'}
                    </button>
                  </span>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}

export default function Settings() {
  const user = useAuthStore((s) => s.user)
  return (
    <div className="space-y-4">
      <div className="card p-4 max-w-xl">
        <h2 className="text-lg font-semibold">Configurações</h2>
        <div className="mt-3 grid grid-cols-1 gap-2 text-sm">
          <div>
            <span className="text-neutral-500">Usuário:</span>{' '}
            <b>{user?.username ?? '—'}</b>
          </div>
          <div>
            <span className="text-neutral-500">Empresa:</span>{' '}
            <b>{user?.company?.name ?? '—'}</b>
          </div>
          <div>
            <span className="text-neutral-500">Perfil:</span>{' '}
            <b>{user?.role ?? '—'}</b>
          </div>
          <div className="flex items-center gap-2 pt-1">
            <span className="text-neutral-500">Idioma:</span>
            <LanguageSwitcher />
          </div>
        </div>
      </div>

      <div className="card p-4">
        <h2 className="text-lg font-semibold">Indicadores do Dashboard</h2>
        <p className="text-sm text-neutral-500 mt-1 mb-3">
          Escolhe, ordena e fixa os indicadores que aparecem no teu dashboard.
        </p>
        <IndicatorsSettings />
      </div>
    </div>
  )
}
